'use client'

import { useEffect, useState } from 'react'

/**
 * Historique de visionnage VIDÉO (dans BLC Video Player).
 * L'API YouTube ne donne pas l'historique YouTube réel → on apprend de ce que
 * l'utilisateur regarde ICI, pour lui proposer des vidéos des chaînes/sujets
 * qu'il regarde (recommandations « Pour toi »).
 */
export type WatchedVideo = {
  videoId: string; title: string; channel?: string; channelId?: string
  thumbnail?: string; ts: number
}

const KEY = 'blc.video.history.v1'
// Plafond relevé à 2000 : un import Google Takeout peut ramener des milliers de
// vidéos ; on n'écrase pas cet historique dès le prochain visionnage (avant, le
// plafond de 300 tronquait l'import). ~2000 entrées ≈ 300 Ko en localStorage.
const MAX_WATCH = 2000

function read(): WatchedVideo[] {
  if (typeof localStorage === 'undefined') return []
  try { return JSON.parse(localStorage.getItem(KEY) || '[]') } catch { return [] }
}
function write(list: WatchedVideo[]) {
  try { localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX_WATCH))); window.dispatchEvent(new Event('blc-video-history')) } catch {}
}

/** Fusionne des vidéos importées avec l'historique existant : dé-doublonne par
 *  videoId (garde le timestamp le plus récent), trie du + récent au + ancien.
 *  Renvoie le nombre AJOUTÉ (nouvelles vidéos). */
export function mergeWatched(items: WatchedVideo[]): number {
  const existing = read()
  const beforeIds = new Set(existing.map(v => v.videoId))
  const byId = new Map<string, WatchedVideo>()
  for (const v of existing) byId.set(v.videoId, v)
  for (const v of items) {
    if (!v?.videoId) continue
    const prev = byId.get(v.videoId)
    if (!prev || v.ts > prev.ts) byId.set(v.videoId, { ...prev, ...v, ts: Math.max(v.ts || 0, prev?.ts || 0) })
  }
  const merged = [...byId.values()].sort((a, b) => b.ts - a.ts)
  write(merged)
  let added = 0
  for (const id of byId.keys()) if (!beforeIds.has(id)) added++
  return added
}

/** Enregistre une vidéo regardée (dé-doublonne, remonte en tête). */
export function logVideoWatch(v: Omit<WatchedVideo, 'ts'>) {
  if (!v?.videoId) return
  const list = read().filter(x => x.videoId !== v.videoId)
  list.unshift({ ...v, ts: Date.now() })
  write(list)
}

/** Lecture synchrone de l'historique (hors hook). */
export function getVideoHistory(): WatchedVideo[] { return read() }

/** Retire UNE vidéo de l'historique. */
export function removeVideoWatch(videoId: string) {
  write(read().filter(x => x.videoId !== videoId))
}

/** Vide tout l'historique de visionnage. */
export function clearVideoHistory() { write([]) }

export function useVideoHistory() {
  const [history, setHistory] = useState<WatchedVideo[]>([])
  useEffect(() => {
    const load = () => setHistory(read())
    load()
    window.addEventListener('blc-video-history', load)
    window.addEventListener('storage', load)
    return () => { window.removeEventListener('blc-video-history', load); window.removeEventListener('storage', load) }
  }, [])
  return { history }
}

// ===== HISTORIQUE DES RECHERCHES =====
export type SearchEntry = { q: string; ts: number }
const SKEY = 'blc.video.searchhistory.v1'

function readSearch(): SearchEntry[] {
  if (typeof localStorage === 'undefined') return []
  try { return JSON.parse(localStorage.getItem(SKEY) || '[]') } catch { return [] }
}
const MAX_SEARCH = 1000
function writeSearch(list: SearchEntry[]) {
  try { localStorage.setItem(SKEY, JSON.stringify(list.slice(0, MAX_SEARCH))); window.dispatchEvent(new Event('blc-video-searchhistory')) } catch {}
}

/** Fusionne des recherches importées (dé-doublonne insensible à la casse, garde
 *  le timestamp le plus récent, trie décroissant). Renvoie le nombre ajouté. */
export function mergeSearches(items: SearchEntry[]): number {
  const existing = readSearch()
  const byLow = new Map<string, SearchEntry>()
  for (const s of existing) byLow.set(s.q.toLowerCase(), s)
  const before = byLow.size
  for (const s of items) {
    const q = (s.q || '').trim(); if (!q) continue
    const low = q.toLowerCase()
    const prev = byLow.get(low)
    if (!prev || s.ts > prev.ts) byLow.set(low, { q, ts: Math.max(s.ts || 0, prev?.ts || 0) })
  }
  const merged = [...byLow.values()].sort((a, b) => b.ts - a.ts)
  writeSearch(merged)
  return byLow.size - before
}

/** Enregistre une recherche (dé-doublonne insensible à la casse, remonte en tête). */
export function logVideoSearch(q: string) {
  const term = (q || '').trim()
  if (!term) return
  const low = term.toLowerCase()
  const list = readSearch().filter(x => x.q.toLowerCase() !== low)
  list.unshift({ q: term, ts: Date.now() })
  writeSearch(list)
}
export function getSearchHistory(): SearchEntry[] { return readSearch() }
export function removeSearch(q: string) {
  const low = (q || '').toLowerCase()
  writeSearch(readSearch().filter(x => x.q.toLowerCase() !== low))
}
export function clearSearchHistory() { writeSearch([]) }

export function useSearchHistory() {
  const [searches, setSearches] = useState<SearchEntry[]>([])
  useEffect(() => {
    const load = () => setSearches(readSearch())
    load()
    window.addEventListener('blc-video-searchhistory', load)
    window.addEventListener('storage', load)
    return () => { window.removeEventListener('blc-video-searchhistory', load); window.removeEventListener('storage', load) }
  }, [])
  return { searches }
}

/** Chaînes les plus regardées (pour aller chercher plus de leurs vidéos). */
export function topChannels(history: WatchedVideo[], n = 4): string[] {
  const count = new Map<string, number>()
  for (const h of history) {
    const c = (h.channel || '').trim()
    if (!c) continue
    count.set(c, (count.get(c) || 0) + 1)
  }
  return [...count.entries()].sort((a, b) => b[1] - a[1]).slice(0, n).map(([c]) => c)
}

/* ===================== Progression (« Reprendre la lecture ») =====================
 * Position de lecture par vidéo, enregistrée par VideoDock pendant la lecture.
 * Une vidéo terminée (> 95 %) est retirée : elle n'a plus à être « reprise ». */
export type VideoProgress = { pos: number; dur: number; ts: number }
const PKEY = 'blc.video.progress.v1'
function readProgress(): Record<string, VideoProgress> {
  if (typeof localStorage === 'undefined') return {}
  try { return JSON.parse(localStorage.getItem(PKEY) || '{}') } catch { return {} }
}
export function saveVideoProgress(videoId: string, pos: number, dur: number) {
  if (!videoId || !(dur > 0)) return
  try {
    const all = readProgress()
    if (pos / dur > 0.95 || pos < 5) delete all[videoId]
    else all[videoId] = { pos: Math.floor(pos), dur: Math.floor(dur), ts: Date.now() }
    // on ne garde que les 200 plus récentes
    const kept = Object.entries(all).sort((a, b) => b[1].ts - a[1].ts).slice(0, 200)
    localStorage.setItem(PKEY, JSON.stringify(Object.fromEntries(kept)))
  } catch {}
}
export function getVideoProgress(videoId: string): VideoProgress | null { return readProgress()[videoId] || null }
export function getAllVideoProgress(): Record<string, VideoProgress> { return readProgress() }
